import { motion } from "framer-motion";
import { User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import { ActionLinkRenderer } from "./ActionLinkRenderer";

interface ChatMessageProps {
  role: "user" | "bot";
  content: string;
  logoUrl?: string;
}

/** Compact markdown renderers optimised for chat bubble sizing */
const markdownComponents: Components = {
  table: ({ children, ...props }) => (
    <div className="chat-md-table-wrap">
      <table {...props} className="chat-md-table">
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead {...props} className="chat-md-thead">
      {children}
    </thead>
  ),
  th: ({ children, ...props }) => (
    <th {...props} className="chat-md-th">
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td {...props} className="chat-md-td">
      {children}
    </td>
  ),
  h1: ({ children, ...props }) => (
    <h1 {...props} className="chat-md-h1">{children}</h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 {...props} className="chat-md-h2">{children}</h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 {...props} className="chat-md-h3">{children}</h3>
  ),
  p: ({ children, ...props }) => (
    <p {...props} className="chat-md-p">{children}</p>
  ),
  strong: ({ children, ...props }) => (
    <strong {...props} className="chat-md-strong">{children}</strong>
  ),
  ul: ({ children, ...props }) => (
    <ul {...props} className="chat-md-ul">{children}</ul>
  ),
  ol: ({ children, ...props }) => (
    <ol {...props} className="chat-md-ol">{children}</ol>
  ),
  li: ({ children, ...props }) => (
    <li {...props} className="chat-md-li">{children}</li>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote {...props} className="chat-md-blockquote">{children}</blockquote>
  ),
  code: ({ children, className, ...props }) => {
    const isBlock = className?.includes("language-");
    if (isBlock) {
      return (
        <pre className="chat-md-pre">
          <code {...props} className="chat-md-code-block">{children}</code>
        </pre>
      );
    }
    return (
      <code {...props} className="chat-md-code-inline">{children}</code>
    );
  },
  hr: (props) => <hr {...props} className="chat-md-hr" />,
  a: ActionLinkRenderer,
};

export function ChatMessage({ role, content, logoUrl = "/claims-app-logo-mini.svg" }: ChatMessageProps) {
  const isUser = role === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`chat-message ${isUser ? "chat-message--user" : "chat-message--bot"}`}
    >
      <div className={`chat-message-inner ${isUser ? "chat-message-inner--user" : "chat-message-inner--bot"}`}>
        {/* Avatar */}
        <div className="chat-avatar-wrap">
          {isUser ? (
            <div className="chat-avatar chat-avatar--user">
              <User size={16} />
            </div>
          ) : (
            <div className="chat-avatar chat-avatar--bot">
              <img
                src={logoUrl}
                alt="Claims Bot"
                className="chat-avatar-img"
              />
            </div>
          )}
        </div>

        {/* Message Bubble */}
        <div className={`chat-bubble ${isUser ? "chat-bubble--user" : "chat-bubble--bot"}`}>
          {isUser ? (
            content
          ) : (
            <div className="chat-md-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
                urlTransform={(url) => {
                  // Preserve app:// protocol links, they're handled by ActionLinkRenderer
                  if (url.startsWith("app://")) return url;
                  // Default sanitization for all other URLs (http, https, mailto, etc.)
                  return url;
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
